import React, { useState } from "react";
import * as XLSX from "xlsx";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";

export default function BalanceByCustomerStaff() {
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [summaryByDate, setSummaryByDate] = useState({});
  const [incomeBreakdownsByDate, setIncomeBreakdownsByDate] = useState({});

  const { data: salesData } = useGetSalesQuery();
  const { data: expensesData } = useGetExpensesQuery({ startDate, endDate });
  const { data: incomesData } = useGetIncomesQuery({ startDate, endDate });
  const { data: depositsData } = useGetDepositsQuery({ startDate, endDate });
  const { data: suppliesData } = useGetSuppliesQuery();

  const getDateRange = (start, end) => {
    const dates = [];
    let current = new Date(start);
    const last = new Date(end);
    while (current <= last) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const filterByDate = (data, date) =>
    data?.filter(item => item.date?.startsWith(date)) || [];

  const groupByCustomerName = (items, key, getName) => {
    return items.reduce((acc, item) => {
      const name = getName(item) || "Unknown";
      acc[name] = (acc[name] || 0) + Number(item[key] || 0);
      return acc;
    }, {});
  };

  const groupByUsername = (items, key) => {
    return items.reduce((acc, item) => {
      const username = item.username || "Unknown";
      acc[username] = (acc[username] || 0) + Number(item[key] || 0);
      return acc;
    }, {});
  };

  const groupIncomeByCategory = (items) => {
    const grouped = { Morning: 0, Afternoon: 0, Night: 0 };
    items.forEach(item => {
      const category = item.category;
      const amount = Number(item.amount || 0);
      if (grouped.hasOwnProperty(category)) {
        grouped[category] += amount;
      }
    });
    return grouped;
  };

  const calculateSummary = () => {
    const dates = getDateRange(startDate, endDate);
    const summary = {};
    const breakdowns = {};

    dates.forEach(date => {
      const sales = groupByCustomerName(
        filterByDate(salesData?.sales, date),
        "totalPrice",
        item => item.customerDetails?.name
      );

      const expenses = groupByCustomerName(
        filterByDate(expensesData?.expenses, date),
        "amount",
        item => item.accountName
      );

      const incomes = filterByDate(incomesData?.incomes, date);
      const incomeTotals = groupByUsername(incomes, "amount");

      const deposits = groupByUsername(filterByDate(depositsData?.deposits, date), "amount");
      const supplies = groupByUsername(filterByDate(suppliesData?.supplies, date), "cardCost");

      breakdowns[date] = groupIncomeByCategory(incomes);

      const allNames = new Set([
        ...Object.keys(sales),
        ...Object.keys(expenses),
        ...Object.keys(incomeTotals),
        ...Object.keys(deposits),
        ...Object.keys(supplies),
      ]);

      const rows = Array.from(allNames).map(name => ({
        name,
        sales: sales[name] || 0,
        expenses: expenses[name] || 0,
        incomes: incomeTotals[name] || 0,
        deposits: deposits[name] || 0,
        supplies: supplies[name] || 0,
      }));

      const totals = rows.reduce(
        (acc, row) => {
          acc.sales += row.sales;
          acc.expenses += row.expenses;
          acc.incomes += row.incomes;
          acc.deposits += row.deposits;
          acc.supplies += row.supplies;
          acc.balance += row.incomes - (row.deposits + row.expenses + row.sales) ;
          
          return acc;
        },
        { sales: 0, expenses: 0, incomes: 0, deposits: 0, supplies: 0, balance: 0 }
      );

      summary[date] = { rows, totals };
    });

    setSummaryByDate(summary);
    setIncomeBreakdownsByDate(breakdowns);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    Object.entries(summaryByDate).forEach(([date, { rows, totals }]) => {
      const sheetData = [
        ...rows.map(row => ({
          Date: date,
          Customer: row.name,
          Sales: row.sales.toFixed(2),
          Expenses: row.expenses.toFixed(2),
          Incomes: row.incomes.toFixed(2),
          Deposits: row.deposits.toFixed(2),
          Supplies: row.supplies.toFixed(2),
        })),
        {
          Date: date,
          Customer: "TOTAL",
          Sales: totals.sales.toFixed(2),
          Expenses: totals.expenses.toFixed(2),
          Incomes: totals.incomes.toFixed(2),
          Deposits: totals.deposits.toFixed(2),
          Supplies: totals.supplies.toFixed(2),
        },
      ];
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, date);
    });

    Object.entries(incomeBreakdownsByDate).forEach(([date, breakdown]) => {
      const sheetData = Object.entries(breakdown).map(([category, amount]) => ({
        Category: category,
        Amount: amount.toFixed(2),
      }));
      const worksheet = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, `${date}_IncomeBreakdown`);
    });

    XLSX.writeFile(workbook, `CustomerSummary_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="range-customer-summary">
      <h2>Summary by Customer and Date</h2>
      <div className="date-range">
        <label>
          Start Date:
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </label>
        <label>
          End Date:
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </label>
      </div>
      <div className="actions">
        <button onClick={calculateSummary}>Generate Summary</button>
        <button onClick={exportToExcel} disabled={Object.keys(summaryByDate).length === 0}  style={{ display: 'none' }}>
          Export to Excel
        </button>
      </div>

      {Object.entries(summaryByDate).map(([date, { rows, totals }]) => (
        <div key={date} className="daily-summary">
          <h3>{date}</h3>
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Sales</th>
                <th>Inc/Exp</th>
                <th>CashCollected</th>
                <th style={{ display: 'none' }}>Deposits</th>
                <th style={{ display: 'none' }}>Supplies</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  <td>{row.sales.toFixed(2)}</td>
                  <td>{row.expenses.toFixed(2)}</td>
                  <td>{row.incomes.toFixed(2)}</td>
                  <td style={{ display: 'none' }}>{row.deposits.toFixed(2)}</td>
                  <td style={{ display: 'none' }}>{row.supplies.toFixed(2)}</td>
                  <td>{(row.sales - row.incomes + row.deposits + row.expenses ).toFixed(2)}</td>
                </tr>
              ))}
              <tr className="totals-row">
                <td><strong>Total</strong></td>
                <td>{totals.sales.toFixed(2)}</td>
                <td>{totals.expenses.toFixed(2)}</td>
                <td>{totals.incomes.toFixed(2)}</td>
                <td style={{ display: 'none' }}>{totals.deposits.toFixed(2)}</td>
                <td style={{ display: 'none' }}>{totals.supplies.toFixed(2)}</td>
                <td>{totals.balance.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <h4>Income Breakdown</h4>
          
<table>
  <thead>
    <tr>
      <th>Category</th>
      <th>Amount</th>
    </tr>
  </thead>
  <tbody>
    {Object.entries(incomeBreakdownsByDate[date] || {}).map(([category, amount]) => (
      <tr key={category}>
        <td>{category}</td>
        <td>{amount.toFixed(2)}</td>
      </tr>
    ))}
    <tr className="totals-row">
      <td><strong>Total</strong></td>
      <td>
        {Object.values(incomeBreakdownsByDate[date] || {})
          .reduce((sum, val) => sum + val, 0)
          .toFixed(2)}
      </td>
    </tr>
  </tbody>
</table>
        </div>
      ))}
    </div>
  );
}